import { useEffect, useRef } from "react";

// Subtle twinkling starfield fixed behind the whole page — shows through the
// transparent header and the semi-transparent sections across the site.
export const Stars = ({ density = 0.00018 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Mostly white with occasional brand cyan/green sparks.
    const palette = ["255,255,255", "255,255,255", "255,255,255", "255,255,255", "0,229,255", "141,198,63"];
    let w = 0, h = 0, stars = [], raf;

    const build = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(280, Math.max(90, Math.floor(w * h * density)));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.3 + 0.3,
        base: Math.random() * 0.45 + 0.2,
        amp: Math.random() * 0.5 + 0.2,
        sp: Math.random() * 1.5 + 0.3,
        ph: Math.random() * Math.PI * 2,
        c: palette[Math.floor(Math.random() * palette.length)],
      }));
    };
    build();
    window.addEventListener("resize", build);

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const time = t / 1000;
      for (const s of stars) {
        const a = reduce ? s.base : s.base + s.amp * (0.5 + 0.5 * Math.sin(time * s.sp + s.ph));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.c},${a})`;
        if (s.r > 1) {
          ctx.shadowColor = `rgba(${s.c},${a})`;
          ctx.shadowBlur = 6;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    if (reduce) draw(0);
    else raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", build);
    };
  }, [density]);

  return (
    <div aria-hidden="true" data-testid="stars-bg" className="pointer-events-none fixed inset-0 z-0">
      <canvas ref={ref} className="h-full w-full" />
    </div>
  );
};
